import type { AccountInfo } from '@azure/msal-browser';
import { ONEDRIVE_CONSUMER } from './oneDrivePickerConstants';

export interface PickerTarget {
  accountType: 'personal' | 'work';
  baseUrl: string;
  pickerPathOverride?: string;
}

export const isConsumerOneDriveResource = (resource: string) => {
  if (!resource) {
    return false;
  }

  try {
    const host = new URL(resource).hostname.toLowerCase();

    return ONEDRIVE_CONSUMER.resourceHosts.some(
      (consumerHost) =>
        host === consumerHost || host.endsWith(`.${consumerHost}`)
    );
  } catch (error) {
    const normalizedResource = resource.toLowerCase();

    return ONEDRIVE_CONSUMER.resourceHosts.some((consumerHost) =>
      normalizedResource.includes(consumerHost)
    );
  }
};

export const isPersonalMicrosoftAccount = (
  account: Pick<
    AccountInfo,
    'tenantId' | 'homeAccountId' | 'username' | 'idTokenClaims'
  >
) => {
  const claims = account.idTokenClaims as Record<string, unknown> | undefined;
  const tenantId = account.tenantId || claims?.tid;
  const domain = account.username?.split('@')[1]?.toLowerCase();

  return (
    tenantId === ONEDRIVE_CONSUMER.tenantId ||
    account.homeAccountId.includes(ONEDRIVE_CONSUMER.tenantId) ||
    claims?.idp === 'live.com' ||
    (typeof domain !== 'undefined' &&
      ONEDRIVE_CONSUMER.accountDomains.includes(domain))
  );
};

export const deriveSharePointBaseUrlFromUsername = (username: string) => {
  const domain = username.split('@')[1];

  if (!domain) {
    throw new Error('Unable to derive tenant URL from signed-in account.');
  }

  const normalizedDomain = domain.toLowerCase();
  const tenantName = normalizedDomain.endsWith('.onmicrosoft.com')
    ? normalizedDomain.replace('.onmicrosoft.com', '')
    : normalizedDomain.split('.')[0];

  if (!tenantName) {
    throw new Error('Unable to derive tenant URL from signed-in account.');
  }

  return `https://${tenantName}.sharepoint.com`;
};

export const buildOneDrivePickerUrl = (
  target: PickerTarget,
  queryString: string
) => {
  const normalizedBaseUrl = target.baseUrl.replace(/\/$/, '');

  if (target.pickerPathOverride) {
    const normalizedPickerPath = target.pickerPathOverride.replace(/^\/+/, '');

    return `${normalizedBaseUrl}/${normalizedPickerPath}?${queryString}`;
  }

  return `${normalizedBaseUrl}/_layouts/15/FilePicker.aspx?${queryString}`;
};
